import { useEffect, useRef, useState, type ReactElement, type RefObject } from 'react';
import type { GeoPoint, MapConfig, ZoneGeometry } from '../api/endpoints';
import { useLocale } from '../i18n/locale';
import {
  contoursFromGeometry,
  geometryFromContours,
  roundCoordinate,
  type Contours,
  type LatLng,
} from '../lib/geometry';
import { useMapConfig } from '../lib/use-map-config';
import {
  loadYandexMaps,
  tokenColor,
  yandexLanguage,
  type YandexGeoObject,
  type YandexMap,
  type YandexMapsApi,
} from '../lib/yandex-maps';

/**
 * Карта справочников на Яндекс Картах (`MapView`, разд. 14 спецификации).
 *
 * Два применения: точка локации (метка, которую ставят щелчком и тянут мышью) и
 * полигон зоны (рисование и правка вершин встроенным редактором 2.1). Остальные зоны
 * показаны бледно — для контекста: зоны пересекаются (FR-503), и рисовать новую,
 * не видя соседних, значит гадать.
 *
 * Карта — удобство, а не единственный путь. Без ключа или с ключом, не разрешённым
 * для домена, панель показывает это словами, а координаты и полигон вводятся без
 * карты: полями широты и долготы и вставкой GeoJSON. Справочник не должен стоять из-за
 * настройки кабинета разработчика Яндекса.
 */

export type MapStatus = 'loading' | 'ready' | 'no_key' | 'failed';

interface MapHandle {
  readonly status: MapStatus;
  readonly ymaps: YandexMapsApi | null;
  readonly map: YandexMap | null;
}

function useYandexMap(container: RefObject<HTMLDivElement | null>): MapHandle {
  const { locale } = useLocale();
  const config = useMapConfig();
  const [handle, setHandle] = useState<MapHandle>({ status: 'loading', ymaps: null, map: null });

  const key = config.status === 'ready' ? config.config.js_api_key : null;
  const settings: MapConfig | null = config.status === 'ready' ? config.config : null;

  useEffect(() => {
    if (config.status === 'loading') {
      return undefined;
    }

    if (settings === null || key === null || key === '') {
      setHandle({ status: 'no_key', ymaps: null, map: null });

      return undefined;
    }

    let cancelled = false;
    let created: YandexMap | null = null;

    loadYandexMaps(key, yandexLanguage(locale))
      .then((ymaps) => {
        const element = container.current;

        if (cancelled || element === null) {
          return;
        }

        created = new ymaps.Map(
          element,
          {
            center: [settings.center.lat, settings.center.lng],
            zoom: settings.zoom,
            controls: ['zoomControl', 'fullscreenControl'],
          },
          // Блок «Открыть в Яндекс Картах» уводит со страницы с несохранённой формой.
          { suppressMapOpenBlock: true, yandexMapDisablePoiInteractivity: true },
        );
        setHandle({ status: 'ready', ymaps, map: created });
      })
      .catch(() => {
        if (!cancelled) {
          setHandle({ status: 'failed', ymaps: null, map: null });
        }
      });

    return () => {
      cancelled = true;
      created?.destroy();
    };
    // Язык карты фиксируется при первой загрузке (см. yandex-maps.ts): смена языка
    // кабинета карту не пересоздаёт.
  }, [config.status, key]);

  return handle;
}

/** Рамка карты и состояние «карты нет» поверх неё. */
function MapFrame({
  container,
  status,
  label,
  height,
  fallback,
}: {
  readonly container: RefObject<HTMLDivElement>;
  readonly status: MapStatus;
  readonly label: string;
  readonly height: string;
  /** Что делать без карты — своё для точки и для зоны. */
  readonly fallback: string;
}): ReactElement {
  const { messages } = useLocale();
  const text = messages.cabinet.map;

  return (
    <div style={{ position: 'relative', minWidth: 0 }}>
      <div
        ref={container}
        role="region"
        aria-label={label}
        style={{
          width: '100%',
          height,
          borderRadius: 'var(--r-md)',
          overflow: 'hidden',
          border: '1px solid var(--line)',
          background: 'var(--surface-sunken)',
          // Пока карты нет, рамка держит место: панель не прыгает, когда карта появится.
          visibility: status === 'ready' ? 'visible' : 'hidden',
        }}
      />
      {status === 'ready' ? null : (
        <div
          role={status === 'loading' ? 'status' : 'note'}
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            padding: 'var(--sp-4)',
            textAlign: 'center',
            borderRadius: 'var(--r-md)',
            border: '1px dashed var(--line-strong)',
            background: status === 'loading' ? 'var(--surface-muted)' : 'var(--warn-weak)',
            color: status === 'loading' ? 'var(--text-muted)' : 'var(--warn)',
            fontSize: 'var(--fs-sm)',
          }}
        >
          <span style={{ display: 'grid', gap: 'var(--sp-1)' }}>
            <strong>
              {status === 'loading'
                ? text.loading
                : status === 'no_key'
                  ? text.noKeyTitle
                  : text.failedTitle}
            </strong>
            {status === 'loading' ? null : (
              <>
                <span>
                  {status === 'no_key'
                    ? text.noKey
                    : text.failed.replace('{host}', window.location.host)}
                </span>
                <span>{fallback}</span>
              </>
            )}
          </span>
        </div>
      )}
    </div>
  );
}

const DEFAULT_HEIGHT = 'clamp(240px, 40vh, 360px)';
/** Масштаб при переходе к точке: квартал с домами, но не подъезд. */
const POINT_ZOOM = 16;

/**
 * Точка на карте: щелчок ставит метку, метку можно перетащить. Изменение точки снаружи
 * (геокодер, поля широты и долготы) переносит метку и центрирует карту.
 */
export function PointPickerMap({
  point,
  onChange,
  label,
  height = DEFAULT_HEIGHT,
  disabled = false,
  onStatus,
}: {
  readonly point: GeoPoint | null;
  readonly onChange: (point: GeoPoint) => void;
  readonly label: string;
  readonly height?: string | undefined;
  readonly disabled?: boolean | undefined;
  readonly onStatus?: ((status: MapStatus) => void) | undefined;
}): ReactElement {
  const { messages } = useLocale();
  const container = useRef<HTMLDivElement>(null);
  const { status, ymaps, map } = useYandexMap(container);
  const placemark = useRef<YandexGeoObject | null>(null);
  const changeRef = useRef(onChange);
  changeRef.current = onChange;

  useEffect(() => onStatus?.(status), [status, onStatus]);

  // Щелчок по карте ставит точку.
  useEffect(() => {
    if (map === null || disabled) {
      return;
    }

    map.events.add('click', (event) => {
      const [lat, lng] = event.get('coords');
      changeRef.current({ lat: roundCoordinate(lat), lng: roundCoordinate(lng) });
    });
  }, [map, disabled]);

  // Метка следует за точкой формы.
  useEffect(() => {
    if (ymaps === null || map === null) {
      return;
    }

    if (point === null) {
      if (placemark.current !== null) {
        map.geoObjects.remove(placemark.current);
        placemark.current = null;
      }

      return;
    }

    const coordinates: [number, number] = [point.lat, point.lng];

    if (placemark.current === null) {
      const created = new ymaps.Placemark(
        coordinates,
        {},
        { draggable: !disabled, preset: 'islands#dotIcon', iconColor: tokenColor('--accent') },
      );

      created.events.add('dragend', () => {
        const [lat, lng] = created.geometry.getCoordinates() as [number, number];
        changeRef.current({ lat: roundCoordinate(lat), lng: roundCoordinate(lng) });
      });
      map.geoObjects.add(created);
      placemark.current = created;
      map.setCenter(coordinates, Math.max(map.getZoom(), POINT_ZOOM));

      return;
    }

    const current = placemark.current.geometry.getCoordinates() as [number, number];

    if (current[0] !== point.lat || current[1] !== point.lng) {
      placemark.current.geometry.setCoordinates(coordinates);
      map.setCenter(coordinates);
    }
  }, [ymaps, map, point?.lat, point?.lng, disabled]);

  return (
    <MapFrame
      container={container}
      status={status}
      label={label}
      height={height}
      fallback={messages.cabinet.map.fallbackPoint}
    />
  );
}

export interface ContextZone {
  readonly id: string;
  readonly name: string;
  readonly geometry: ZoneGeometry | null | undefined;
}

/**
 * Полигон зоны: рисование и правка вершин.
 *
 * `drawRequest` — счётчик: каждое его увеличение начинает рисование заново (кнопка
 * «Нарисовать заново»). Полигон, пришедший снаружи (вставка GeoJSON, «Очистить»),
 * перерисовывается; изменение, пришедшее из самого редактора, — нет, иначе каждое
 * перетаскивание вершины пересоздавало бы полигон под курсором.
 *
 * Мультиполигон показывается, но вершины не правятся: редактор 2.1 правит один
 * полигон. Его заменяют, нарисовав зону заново или вставив GeoJSON.
 */
export function ZoneEditorMap({
  geometry,
  onChange,
  contextZones,
  drawRequest,
  label,
  height = DEFAULT_HEIGHT,
  disabled = false,
  onStatus,
}: {
  readonly geometry: ZoneGeometry | null;
  readonly onChange: (geometry: ZoneGeometry | null) => void;
  readonly contextZones: readonly ContextZone[];
  readonly drawRequest: number;
  readonly label: string;
  readonly height?: string | undefined;
  readonly disabled?: boolean | undefined;
  readonly onStatus?: ((status: MapStatus) => void) | undefined;
}): ReactElement {
  const { messages } = useLocale();
  const container = useRef<HTMLDivElement>(null);
  const { status, ymaps, map } = useYandexMap(container);
  const editable = useRef<YandexGeoObject[]>([]);
  const emitted = useRef<string | null>(null);
  const changeRef = useRef(onChange);
  changeRef.current = onChange;
  const fitted = useRef(false);

  useEffect(() => onStatus?.(status), [status, onStatus]);

  // Соседние зоны — бледно и без реакции на мышь: они контекст, а не цель.
  useEffect(() => {
    if (ymaps === null || map === null) {
      return undefined;
    }

    const muted = tokenColor('--text-faint');
    const objects = contextZones.flatMap((zone) =>
      contoursFromGeometry(zone.geometry).map(
        (contours) =>
          new ymaps.Polygon(
            contours,
            { hintContent: zone.name },
            {
              fillColor: muted,
              fillOpacity: CONTEXT_FILL_OPACITY,
              strokeColor: muted,
              strokeWidth: 1,
              strokeStyle: 'shortdash',
              interactivityModel: 'default#transparent',
            },
          ),
      ),
    );

    objects.forEach((object) => map.geoObjects.add(object));

    return () => objects.forEach((object) => map.geoObjects.remove(object));
  }, [ymaps, map, contextZones]);

  // Свой полигон: пересоздаётся, только если геометрия пришла не из редактора.
  useEffect(() => {
    if (ymaps === null || map === null) {
      return;
    }

    const serialized = JSON.stringify(geometry);

    if (serialized === emitted.current && editable.current.length > 0) {
      return;
    }

    clear(map, editable.current);
    editable.current = [];
    emitted.current = serialized;

    const polygons = contoursFromGeometry(geometry);

    polygons.forEach((contours) => {
      const polygon = createEditable(ymaps, contours);
      map.geoObjects.add(polygon);
      editable.current.push(polygon);
    });

    const [single] = editable.current;

    if (polygons.length === 1 && single !== undefined && !disabled) {
      watch(single, (next) => {
        emitted.current = JSON.stringify(next);
        changeRef.current(next);
      });
      single.editor?.startEditing();
    }

    // Карта приезжает к зоне один раз — при первом показе; дальше её двигает человек.
    if (!fitted.current && polygons.length > 0) {
      const bounds = map.geoObjects.getBounds();

      if (bounds !== null) {
        map.setBounds(bounds, { checkZoomRange: true, zoomMargin: FIT_MARGIN });
        fitted.current = true;
      }
    }
  }, [ymaps, map, geometry, disabled]);

  // «Нарисовать заново»: прежний полигон убирается, редактор начинает с пустого.
  useEffect(() => {
    if (ymaps === null || map === null || drawRequest === 0 || disabled) {
      return;
    }

    clear(map, editable.current);

    const polygon = createEditable(ymaps, []);
    map.geoObjects.add(polygon);
    editable.current = [polygon];
    emitted.current = JSON.stringify(null);
    changeRef.current(null);

    watch(polygon, (next) => {
      emitted.current = JSON.stringify(next);
      changeRef.current(next);
    });
    polygon.editor?.startDrawing();
    // Рисование запускает только счётчик; карта и API в зависимостях — чтобы
    // запрос, сделанный до загрузки карты, выполнился, когда она загрузится.
  }, [drawRequest, ymaps, map, disabled]);

  return (
    <MapFrame
      container={container}
      status={status}
      label={label}
      height={height}
      fallback={messages.cabinet.map.fallbackZone}
    />
  );

  function createEditable(api: YandexMapsApi, contours: Contours | []): YandexGeoObject {
    const accent = tokenColor('--accent');

    return new api.Polygon(
      contours,
      {},
      {
        fillColor: accent,
        fillOpacity: EDIT_FILL_OPACITY,
        strokeColor: accent,
        strokeWidth: EDIT_STROKE_WIDTH,
        editorDrawingCursor: 'crosshair',
      },
    );
  }
}

/** Прозрачность заливки: своя зона заметна, соседние почти не мешают подложке. */
const EDIT_FILL_OPACITY = 0.2;
const CONTEXT_FILL_OPACITY = 0.08;
const EDIT_STROKE_WIDTH = 3;
const FIT_MARGIN = 24;

function clear(map: YandexMap, objects: readonly YandexGeoObject[]): void {
  objects.forEach((object) => {
    object.editor?.stopDrawing();
    object.editor?.stopEditing();
    map.geoObjects.remove(object);
  });
}

function watch(polygon: YandexGeoObject, emit: (geometry: ZoneGeometry | null) => void): void {
  polygon.geometry.events.add('change', () => {
    const contours = polygon.geometry.getCoordinates() as LatLng[][];
    emit(geometryFromContours(contours));
  });
}
